<template>
  <div class="law-cases">
    <div class="law-cases__header filters-bar">
      <law-cases-filter />
      <i-button
        v-if="getCommonModulePerms('law_cases', 'create')"
        type="success"
        @click="createLawCase"
      >
        Создать судебное дело
      </i-button>
    </div>
    <card dis-hover :padding="8" class="law-cases__stats stats-block special">
      <inline-loader :loading="statsLoading" v-if="statsLoading" />
      <law-cases-stats v-else :stats="stats" />
    </card>
    <inline-loader :loading="loading" v-if="loading && !nodata" />
    <div class="ms-nodata ms-text" v-else-if="!loading && nodata">
      Не удалось загрузить список судебных дел. Повторите, пожалуйста, попытку
      позже
    </div>
    <law-cases-list
      :law-cases="lawCases"
      @open-law-case="openLawCase"
      v-else-if="lawCases.length"
    />
    <div v-else class="ms-nodata ms-text">
      Судебные дела отсутствуют
    </div>

    <modal
      v-if="!loading"
      class-name="law-cases-editor editor-modal"
      v-model="lawCaseModal"
      width="840"
      :styles="{ top: '40px' }"
      :mask-closable="false"
    >
      <lc-editor-header slot="header" :law-case="lawCase" />
      <div class="law-cases-editor__container">
        <row :gutter="16">
          <i-col span="12" class="law-cases-editor__content">
            <normal-card title="Общая информация" icon="md-information-circle">
              <lc-editor-contract :contract="contract" />
              <lc-editor-info
                :law-case="lawCase"
                :companies="companies"
                :law-case-statuses="lawCaseStatuses"
              />
              <resources-editor type="law_cases" :parent="lawCase" />
            </normal-card>
          </i-col>
          <i-col span="12">
            <lc-editor-stages-controls
              :law-case="lawCase"
              @add-law-case-stage="addLawCaseStage"
            />
            <div class="lc-editor-stages">
              <ul class="lc-editor-stages-list" v-if="filteredStages.length">
                <lc-editor-stage
                  v-for="(stage, i) in filteredStages"
                  :key="stage.id"
                  :stage="stage"
                  :i="i"
                  @delete-stage="deleteLawCaseStage"
                />
              </ul>
              <div v-else class="ms-nodata ms-help">
                События отсутствуют
              </div>
            </div>
          </i-col>
        </row>
      </div>
      <div slot="footer" class="law-cases-editor__footer">
        <i-button
          :disabled="!lawCase.id || !getCommonModulePerms('law_cases', 'delete')"
          type="error"
          size="small"
          :loading="deleting"
          @click="deleteLawCase"
        >
          Удалить
        </i-button>
        <i-button
          type="success"
          size="small"
          :loading="saving"
          :disabled="!isSaveAvailable || !getCommonModulePerms('law_cases', 'update')"
          @click="saveLawCase"
        >
          Сохранить
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import LawCasesList from '@/components/law-cases/law-cases-list'
import InlineLoader from '@/components/loaders/inline-loader'
import LawCasesStats from '@/components/stats/law-cases-stats'
import NormalCard from '@/components/parts/normal-card'
import LcEditorHeader from '@/components/law-cases/editor/lc-editor-header'
import LcEditorContract from '@/components/law-cases/editor/lc-editor-contract'
import LcEditorInfo from '@/components/law-cases/editor/lc-editor-info'
import LcEditorStage from '@/components/law-cases/editor/lc-editor-stage'
import LcEditorStagesControls from '@/components/law-cases/editor/lc-editor-stages-controls'
import LawCasesFilter from '@/components/filters/law-cases'
import ResourcesEditor from '@/components/resources/editor/resources-editor-simple'
import { lawCase, lawCaseStage } from '@/config/law-cases'
import { getLawCasesUrl } from '@/config/filters'
import { formatDefaultDate } from '@/utils'

export default {
  props: ['type'],
  components: {
    LawCasesList,
    InlineLoader,
    LawCasesStats,
    NormalCard,
    LcEditorHeader,
    LcEditorContract,
    LcEditorInfo,
    LcEditorStage,
    LcEditorStagesControls,
    LawCasesFilter,
    ResourcesEditor
  },
  data () {
    return {
      stats: {},
      statsLoading: false,
      loading: false,
      nodata: false,
      contract: null,
      lawCaseStatuses: [],
      lawCases: [],
      companies: [],
      lawCaseModal: false,
      deleting: false,
      saving: false,
      lawCase
    }
  },
  computed: {
    isSaveAvailable () {
      const {
        applicant_id,
        defendant_id,
        start_date,
        law_status_id,
        law_case_stages
      } = this.lawCase

      if (!applicant_id || !defendant_id || !start_date || !law_status_id) {
        return false
      }
      if (law_case_stages.some((s) => !s.name || !s.description)) return false

      return true
    },
    filteredStages () {
      return this.lawCase.law_case_stages.filter((s) => !s.disabled)
    }
  },
  watch: {
    '$store.state.filters.lawCases': {
      handler: function () {
        this.getLawCases()
      },
      deep: true
    }
    // '$store.state.socket': {
    //   handler: function (val) {
    //     if (!val.create && !val.update && !val.delete) return
    //     const { type } = this.$store.state.socket

    //     if (type === 'law_cases') {
    //       this.getLawCases()
    //       this.getStats()
    //     }

    //     this.$store.commit('CLEAR_SOCKET_STATE')
    //   },
    //   deep: true,
    //   immediate: true
    // }
  },
  methods: {
    async openLawCase ({ id }) {
      try {
        const url = `page/law_case/${id}`
        const lawCase = await this.$store.dispatch('GET_REQUEST', { url })

        if (lawCase.start_date) { lawCase.start_date = formatDefaultDate(lawCase.start_date) }
        if (lawCase.end_date) { lawCase.end_date = formatDefaultDate(lawCase.end_date) }

        lawCase.law_case_stages = lawCase.law_case_stages.map((s) => ({
          ...s,
          start_date: s.start_date ? formatDefaultDate(s.start_date) : null,
          end_date: s.end_date ? formatDefaultDate(s.end_date) : null
        }))

        this.lawCase = lawCase
        this.lawCaseModal = true
      } catch (error) {
        console.log(error)
      }
    },
    createLawCase () {
      const status = this.lawCaseStatuses.find((s) => s.name === 'Активно')
      this.$set(this, 'lawCase', cloneDeep(lawCase))
      if (status) this.lawCase.law_status_id = status.id
      this.lawCase.applicant_id = this.contract.performer_id
      this.lawCase.start_date = new Date()
      this.lawCaseModal = true
    },
    addLawCaseStage () {
      this.lawCase.law_case_stages.push({ ...lawCaseStage })
    },
    deleteLawCaseStage ({ index, id }) {
      if (!id) this.lawCase.law_case_stages.splice(index, 1)
      else this.$set(this.lawCase.law_case_stages[index], 'disabled', true)
    },
    async getLawCases () {
      const { id } = this.$route.params

      try {
        const url = getLawCasesUrl(
          `page/law_case?contract_id=${id}`,
          this.$store.state.filters.lawCases
        )
        this.lawCases = await this.$store.dispatch('GET_REQUEST', { url })
      } catch (error) {
        console.log(error)
      }
    },
    async getStats () {
      const { id } = this.$route.params

      try {
        this.statsLoading = true
        const url = `page/law_case_stat/${id}`
        this.stats = await this.$store.dispatch('GET_REQUEST', { url })
        this.statsLoading = false
      } catch (error) {
        console.log(error)
        this.statsLoading = false
      }
    },
    async getData () {
      const { id } = this.$route.params

      try {
        this.loading = true
        const contractUrl = `page/${this.type}/${id}`
        const lawCaseStatusesUrl = `objectInfo/law_statuses`
        const applicantsUrl = `page/own_company`
        const defendantsUrl = `page/other_company`

        this.contract = await this.$store.dispatch('GET_REQUEST', {
          url: contractUrl
        })
        this.lawCaseStatuses = await this.$store.dispatch('GET_REQUEST', {
          url: lawCaseStatusesUrl
        })
        const applicants = await this.$store.dispatch('GET_REQUEST', {
          url: applicantsUrl
        })
        const defendants = await this.$store.dispatch('GET_REQUEST', {
          url: defendantsUrl
        })
        this.companies = [...applicants, ...defendants]
        await this.getLawCases()

        this.loading = false
      } catch (error) {
        console.log(error)
        this.loading = false
        this.nodata = true
      }
    },
    getLawCaseData () {
      const {
        id,
        law_status_id,
        applicant_id,
        defendant_id,
        start_date,
        end_date,
        costs,
        law_case_stages,
        law_info,
        number,
        object_resources
      } = this.lawCase

      return {
        id,
        law_status_id,
        applicant_id,
        defendant_id,
        start_date: moment(start_date).format(),
        end_date: moment(end_date).format(),
        costs,
        law_case_stages,
        object_resources,
        law_info,
        number,
        contract_id: this.contract.id
      }
    },
    async deleteLawCase () {
      try {
        this.deleting = true
        const url = `page/law_case?id=${this.lawCase.id}`
        await this.$store.dispatch('DELETE_REQUEST', { url })

        this.updateData()

        this.$Message.success('Удаление данных выполнено успешно')
        this.deleting = false
        this.lawCaseModal = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при удалении данных')
        this.deleting = false
      }
    },
    async saveLawCase () {
      const { id } = this.$route.params

      try {
        this.saving = true
        const url = `page/law_case?contract_id=${id}`
        const data = this.getLawCaseData()

        await this.$store.dispatch('POST_REQUEST', { url, data })

        this.updateData()

        this.$set(this, 'lawCase', cloneDeep(lawCase))
        this.$Message.success('Сохранение данных выполнено успешно')
        this.saving = false
        this.lawCaseModal = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при сохранении данных')
        this.saving = false
      }
    },
    updateData () {
      this.getLawCases()
      this.getStats()
    }
  },
  async created () {
    await this.getData()
    this.getStats()

    const { open } = this.$route.query
    if (open) this.openLawCase({ id: open })
  }
}
</script>

<style lang="less">
.law-cases {
  &__stats {
    margin-bottom: 16px;
    width: 66%;
  }
}

.law-cases-editor {
  &__content {
    max-height: 70vh;
    overflow: auto;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
  }
}
</style>
